(() => {
  const initialiseBuilder = (builder) => {
    if (!builder || builder.dataset.builderInitialised === 'true') {
      return;
    }

    builder.dataset.builderInitialised = 'true';

    const quantityInputs = builder.querySelectorAll('.builder-quantity');
    const subscribeButton = builder.querySelector('[data-subscribe-button]');
    const message = builder.querySelector('.builder-message');
    const popup = document.getElementById(builder.dataset.popupId);

    const popupCloseButtons = popup?.querySelectorAll(
      '[data-close-wholesale-popup]'
    );

    const wholesaleThreshold =
      Number.parseInt(builder.dataset.wholesaleThreshold || '10', 10) || 10;

    let popupWasShown = false;
    let isSubmitting = false;

    /*
     * ==========================================================
     * APPSTLE BUNDLE RULES
     * ==========================================================
     */

    const parsePossibleJson = (value) => {
      if (typeof value !== 'string') {
        return value;
      }

      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const getAppstleRules = () => {
      const rulesElementId = builder.dataset.appstleRulesId;

      if (!rulesElementId) {
        return [];
      }

      const rulesElement = document.getElementById(rulesElementId);

      if (!rulesElement) {
        return [];
      }

      try {
        const parsed = JSON.parse(
          rulesElement.textContent?.trim() || '[]'
        );

        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error(
          'Could not parse Appstle bundle rules:',
          error
        );

        return [];
      }
    };

    const containsVariantId = (value, variantId) => {
      const parsedValue = parsePossibleJson(value);

      if (parsedValue == null) {
        return false;
      }

      if (
        typeof parsedValue === 'number' ||
        typeof parsedValue === 'string'
      ) {
        return String(parsedValue) === String(variantId);
      }

      if (Array.isArray(parsedValue)) {
        return parsedValue.some((item) =>
          containsVariantId(item, variantId)
        );
      }

      if (typeof parsedValue === 'object') {
        return Object.values(parsedValue).some((item) =>
          containsVariantId(item, variantId)
        );
      }

      return false;
    };

    const findVolumeDiscountRule = (variantId) => {
      const rules = getAppstleRules();

      const activeVolumeRules = rules.filter((rule) => {
        return (
          rule?.bundleType === 'VOLUME_DISCOUNT' &&
          (!rule.status || rule.status === 'ACTIVE')
        );
      });

      let matchingRule = activeVolumeRules.find((rule) => {
        return containsVariantId(
          rule?.variants,
          variantId
        );
      });

      if (matchingRule) {
        return matchingRule;
      }

      matchingRule = activeVolumeRules.find((rule) => {
        return containsVariantId(
          rule?.products,
          variantId
        );
      });

      return matchingRule || null;
    };

    /*
     * ==========================================================
     * UI / MESSAGES / WHOLESALE POPUP
     * ==========================================================
     */

    const showMessage = (text, isError = false) => {
      if (!message) return;

      message.hidden = false;
      message.textContent = text;
      message.classList.toggle('is-error', isError);
    };

    const clearMessage = () => {
      if (!message) return;

      message.hidden = true;
      message.textContent = '';
      message.classList.remove('is-error');
    };

    const openWholesalePopup = () => {
      if (!popup) return;

      popup.hidden = false;
      popup.classList.add('is-visible');
      document.body.classList.add('builder-popup-open');
    };

    const closeWholesalePopup = () => {
      if (!popup) return;

      popup.classList.remove('is-visible');
      document.body.classList.remove('builder-popup-open');

      window.setTimeout(() => {
        popup.hidden = true;
      }, 200);
    };

    popupCloseButtons?.forEach((button) => {
      button.addEventListener('click', closeWholesalePopup);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && popup && !popup.hidden) {
        closeWholesalePopup();
      }
    });

    /*
     * ==========================================================
     * QUANTITY CONTROLS
     * ==========================================================
     */

    const getInputQuantity = (input) => {
      const quantity = Number.parseInt(input?.value || '0', 10);

      if (Number.isNaN(quantity) || quantity < 0) {
        return 0;
      }

      return quantity;
    };

    const getTotalQuantity = () => {
      let totalQuantity = 0;

      quantityInputs.forEach((input) => {
        totalQuantity += getInputQuantity(input);
      });

      return totalQuantity;
    };

    const checkWholesaleThreshold = () => {
      const totalQuantity = getTotalQuantity();

      if (
        totalQuantity > wholesaleThreshold &&
        !popupWasShown
      ) {
        popupWasShown = true;
        openWholesalePopup();
      }

      if (totalQuantity <= wholesaleThreshold) {
        popupWasShown = false;
      }
    };

    const updateSummary = (input) => {
      const rawValue = input.value.trim();
      let quantity = 0;

      if (rawValue !== '') {
        quantity = Number.parseInt(rawValue, 10);

        if (Number.isNaN(quantity) || quantity < 0) {
          quantity = 0;
        }

        input.value = quantity;
      }

      const summaryId = input.dataset.summaryTarget;
      const summaryValue = document.getElementById(summaryId);

      if (summaryValue) {
        summaryValue.textContent = quantity;
      }

      checkWholesaleThreshold();
    };

    quantityInputs.forEach((input) => {
      const control = input.closest(
        '.builder-quantity-control'
      );

      const increaseButton = control?.querySelector(
        '[data-quantity-increase]'
      );

      const decreaseButton = control?.querySelector(
        '[data-quantity-decrease]'
      );

      const changeQuantity = (amount) => {
        const currentQuantity = getInputQuantity(input);

        const newQuantity = Math.max(
          0,
          currentQuantity + amount
        );

        input.value =
          newQuantity === 0
            ? ''
            : String(newQuantity);

        updateSummary(input);
      };

      increaseButton?.addEventListener('click', () => {
        changeQuantity(1);
      });

      decreaseButton?.addEventListener('click', () => {
        changeQuantity(-1);
      });

      input.addEventListener('input', () => {
        updateSummary(input);
      });

      input.addEventListener('change', () => {
        updateSummary(input);
      });

      updateSummary(input);
    });

    /*
     * ==========================================================
     * CART HELPERS
     * ==========================================================
     */

    const wait = (milliseconds) => {
      return new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds);
      });
    };

    const getCart = async () => {
      const response = await fetch(
        `${window.Shopify.routes.root}cart.js?t=${Date.now()}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          },
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        throw new Error(
          'The cart could not be loaded.'
        );
      }

      return response.json();
    };

    const removeBuilderVariantsFromCart = async (
      builderVariantIds
    ) => {
      const currentCart = await getCart();
      const updates = {};

      currentCart.items.forEach((cartItem) => {
        if (
          builderVariantIds.has(
            Number(cartItem.variant_id)
          )
        ) {
          updates[cartItem.key] = 0;
        }
      });

      if (Object.keys(updates).length === 0) {
        return;
      }

      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            updates
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Existing pack quantities could not be reset.'
        );
      }

      await wait(200);
      await getCart();
    };

    /*
     * ==========================================================
     * ADD SUBSCRIPTION ITEM
     * ==========================================================
     */

    const addSubscriptionItem = async (item) => {
      const appstleRule =
        findVolumeDiscountRule(item.id);

      if (!appstleRule) {
        console.error(
          `No active Appstle Volume Discount rule found for variant ${item.id}.`
        );

        console.error(
          'Available Appstle rules:',
          getAppstleRules()
        );

        throw new Error(
          'The multi-pack pricing rule could not be found for one of the selected packs.'
        );
      }

      if (!appstleRule.uniqueRef) {
        throw new Error(
          'The Appstle multi-pack pricing rule is missing its unique reference.'
        );
      }

      const properties = {
        '_appstle-bb-id':
          String(appstleRule.uniqueRef),

        '_appstle_bundles_type':
          'VOLUME_DISCOUNT',

        '__appstle-bb-name':
          String(
            appstleRule.name ||
            appstleRule.title ||
            'Ziggamins Multi-Pack Saving'
          )
      };

      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: item.id,
                quantity: item.quantity,
                selling_plan:
                  item.selling_plan,
                properties
              }
            ]
          })
        }
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({}));

        throw new Error(
          errorData.description ||
            errorData.message ||
            'A subscription item could not be added.'
        );
      }

      await wait(150);

      let cart = await getCart();

      let matchingItem = cart.items.find(
        (cartItem) =>
          Number(cartItem.variant_id) ===
            Number(item.id) &&
          String(
            cartItem.properties?.[
              '_appstle-bb-id'
            ] || ''
          ) ===
            String(appstleRule.uniqueRef)
      );

      if (!matchingItem) {
        throw new Error(
          'A selected multi-pack subscription item is missing from the cart.'
        );
      }

      let currentSellingPlanId =
        matchingItem.selling_plan_allocation
          ?.selling_plan?.id;

      /*
       * Repair the selling plan if it was somehow lost.
       */
      if (
        Number(currentSellingPlanId) !==
        Number(item.selling_plan)
      ) {
        const repairResponse = await fetch(
          `${window.Shopify.routes.root}cart/change.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              id: matchingItem.key,
              quantity: matchingItem.quantity,
              selling_plan:
                item.selling_plan,
              properties:
                matchingItem.properties
            })
          }
        );

        if (!repairResponse.ok) {
          const repairError = await repairResponse
            .json()
            .catch(() => ({}));

          throw new Error(
            repairError.description ||
              repairError.message ||
              'A subscription item could not be corrected.'
          );
        }

        await wait(200);

        cart = await getCart();

        matchingItem = cart.items.find(
          (cartItem) =>
            Number(cartItem.variant_id) ===
              Number(item.id) &&
            String(
              cartItem.properties?.[
                '_appstle-bb-id'
              ] || ''
            ) ===
              String(appstleRule.uniqueRef)
        );

        currentSellingPlanId =
          matchingItem
            ?.selling_plan_allocation
            ?.selling_plan
            ?.id;
      }

      if (
        Number(currentSellingPlanId) !==
        Number(item.selling_plan)
      ) {
        throw new Error(
          'One of the selected packs could not be added as a subscription.'
        );
      }

      return {
        ...item,
        appstleRule
      };
    };

    /*
     * ==========================================================
     * VERIFY FINAL CART
     * ==========================================================
     */

    const verifyFinalCart = async (
      addedItems
    ) => {
      const finalCart = await getCart();

      for (const selectedItem of addedItems) {
        const matchingItem =
          finalCart.items.find(
            (cartItem) =>
              Number(
                cartItem.variant_id
              ) ===
                Number(
                  selectedItem.id
                ) &&

              String(
                cartItem.properties?.[
                  '_appstle-bb-id'
                ] || ''
              ) ===
                String(
                  selectedItem
                    .appstleRule
                    .uniqueRef
                )
          );

        if (!matchingItem) {
          throw new Error(
            'One of the selected multi-pack subscriptions is missing from the cart.'
          );
        }

        const sellingPlanId =
          matchingItem
            .selling_plan_allocation
            ?.selling_plan
            ?.id;

        if (
          Number(sellingPlanId) !==
          Number(
            selectedItem.selling_plan
          )
        ) {
          throw new Error(
            'One of the selected packs was not added as a subscription.'
          );
        }

        if (
          Number(matchingItem.quantity) !==
          Number(
            selectedItem.quantity
          )
        ) {
          throw new Error(
            'One of the selected pack quantities is incorrect.'
          );
        }

        if (
          matchingItem.properties?.[
            '_appstle_bundles_type'
          ] !== 'VOLUME_DISCOUNT'
        ) {
          throw new Error(
            'One of the selected packs is missing its multi-pack pricing information.'
          );
        }
      }

      return finalCart;
    };

    /*
     * ==========================================================
     * FORCE REAL APPSTLE RECALCULATION
     * ==========================================================
     *
     * This performs the same real cart mutation that manually
     * pressing + and then - was doing.
     *
     * Example:
     *
     * Customer selected 4
     *
     * Behind the scenes:
     *
     * 4 -> 5 -> 4
     *
     * The customer never sees 5.
     *
     * This is done separately for EVERY selected variant.
     * ==========================================================
     */

    const forceAppstleLineRecalculation = async (
      addedItems
    ) => {
      for (const selectedItem of addedItems) {
        let cart = await getCart();

        let matchingItem = cart.items.find(
          (cartItem) =>
            Number(cartItem.variant_id) ===
              Number(selectedItem.id) &&
            Number(
              cartItem
                .selling_plan_allocation
                ?.selling_plan
                ?.id
            ) ===
              Number(
                selectedItem.selling_plan
              ) &&
            String(
              cartItem.properties?.[
                '_appstle-bb-id'
              ] || ''
            ) ===
              String(
                selectedItem
                  .appstleRule
                  .uniqueRef
              )
        );

        if (!matchingItem) {
          throw new Error(
            'A subscription item could not be refreshed for multi-pack pricing.'
          );
        }

        const originalQuantity =
          Number(selectedItem.quantity);

        const temporaryQuantity =
          originalQuantity + 1;

        /*
         * STEP A:
         * Temporarily increase by 1.
         */
        let response = await fetch(
          `${window.Shopify.routes.root}cart/change.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              id: matchingItem.key,
              quantity:
                temporaryQuantity
            })
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({}));

          throw new Error(
            errorData.description ||
              errorData.message ||
              'Multi-pack pricing could not be recalculated.'
          );
        }

        /*
         * Give Shopify/Appstle time to process
         * the first REAL cart mutation.
         */
        await wait(350);

        cart = await getCart();

        matchingItem = cart.items.find(
          (cartItem) =>
            Number(cartItem.variant_id) ===
              Number(selectedItem.id) &&
            Number(
              cartItem
                .selling_plan_allocation
                ?.selling_plan
                ?.id
            ) ===
              Number(
                selectedItem.selling_plan
              ) &&
            String(
              cartItem.properties?.[
                '_appstle-bb-id'
              ] || ''
            ) ===
              String(
                selectedItem
                  .appstleRule
                  .uniqueRef
              )
        );

        if (!matchingItem) {
          throw new Error(
            'A subscription item disappeared during multi-pack recalculation.'
          );
        }

        /*
         * STEP B:
         * Restore the exact quantity originally selected.
         */
        response = await fetch(
          `${window.Shopify.routes.root}cart/change.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              id: matchingItem.key,
              quantity:
                originalQuantity
            })
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({}));

          throw new Error(
            errorData.description ||
              errorData.message ||
              'The selected quantity could not be restored after multi-pack recalculation.'
          );
        }

        /*
         * Give Shopify/Appstle time to process
         * the restored final quantity.
         */
        await wait(450);

        await getCart();
      }

      /*
       * Final settlement after ALL variants
       * have been independently recalculated.
       */
      await wait(500);

      await getCart();
    };

    /*
     * ==========================================================
     * SUBSCRIBE BUTTON
     * ==========================================================
     */

    subscribeButton?.addEventListener(
      'click',
      async () => {
        if (isSubmitting) {
          return;
        }

        clearMessage();

        const cards = Array.from(
          builder.querySelectorAll(
            '.builder-card'
          )
        );

        const items = [];

        const builderVariantIds =
          new Set();

        let missingVariant = false;
        let missingSellingPlan = false;

        for (const card of cards) {
          const input =
            card.querySelector(
              '.builder-quantity'
            );

          const quantity =
            getInputQuantity(input);

          const variantId =
            Number.parseInt(
              card.dataset.variantId ||
                '',
              10
            );

          const sellingPlanId =
            Number.parseInt(
              card.dataset.sellingPlanId ||
                '',
              10
            );

          if (variantId) {
            builderVariantIds.add(
              variantId
            );
          }

          if (quantity <= 0) {
            continue;
          }

          if (!variantId) {
            missingVariant = true;
            continue;
          }

          if (!sellingPlanId) {
            missingSellingPlan =
              true;

            continue;
          }

          items.push({
            id: variantId,
            quantity,
            selling_plan:
              sellingPlanId
          });
        }

        if (missingVariant) {
          showMessage(
            'A selected product variant could not be identified.',
            true
          );

          return;
        }

        if (missingSellingPlan) {
          showMessage(
            'One of the selected packs is not connected to the monthly subscription plan.',
            true
          );

          return;
        }

        if (items.length === 0) {
          showMessage(
            'Please select at least one pack.',
            true
          );

          return;
        }

        /*
         * Check Appstle rules before touching the cart.
         */
        for (const item of items) {
          const rule =
            findVolumeDiscountRule(
              item.id
            );

          if (!rule) {
            console.error(
              'Available Appstle rules:',
              getAppstleRules()
            );

            showMessage(
              'A multi-pack pricing rule could not be found for one of the selected packs.',
              true
            );

            return;
          }
        }

        isSubmitting = true;

        subscribeButton.disabled =
          true;

        subscribeButton.setAttribute(
          'aria-busy',
          'true'
        );

        showMessage(
          'Adding your subscription…'
        );

        try {
          /*
           * STEP 1
           *
           * Remove all old versions of both builder variants.
           */
          await removeBuilderVariantsFromCart(
            builderVariantIds
          );

          /*
           * STEP 2
           *
           * Add each variant separately with:
           *
           * - selling plan
           * - correct quantity
           * - Appstle Volume Discount rule metadata
           */
          const addedItems = [];

          for (const item of items) {
            const addedItem =
              await addSubscriptionItem(
                item
              );

            addedItems.push(
              addedItem
            );
          }

          /*
           * STEP 3
           *
           * Make sure everything was added correctly
           * before forcing recalculation.
           */
          await verifyFinalCart(
            addedItems
          );

          /*
           * STEP 4
           *
           * Force the exact same recalculation that manually
           * pressing + then - was triggering.
           */
          showMessage(
            'Applying your multi-pack savings…'
          );

          await forceAppstleLineRecalculation(
            addedItems
          );

          /*
           * STEP 5
           *
           * Final verification after all +1 -> original
           * quantity recalculations.
           */
          await verifyFinalCart(
            addedItems
          );

          /*
           * One last settlement period before showing cart.
           */
          await wait(500);

          await getCart();

          showMessage(
            'Subscription added successfully.'
          );

          /*
           * Force a completely fresh cart-page request.
           */
          window.location.href =
            `${window.Shopify.routes.root}cart?refresh=${Date.now()}`;
        } catch (error) {
          console.error(
            'Subscription builder error:',
            error
          );

          showMessage(
            error instanceof Error
              ? error.message
              : 'Something went wrong. Please try again.',
            true
          );

          isSubmitting = false;

          subscribeButton.disabled =
            false;

          subscribeButton.removeAttribute(
            'aria-busy'
          );
        }
      }
    );
  };

  /*
   * ==========================================================
   * INITIALISATION
   * ==========================================================
   */

  const initialiseAllBuilders = (
    root = document
  ) => {
    if (
      root instanceof Element &&
      root.matches(
        '.custom-subscription-builder'
      )
    ) {
      initialiseBuilder(root);
    }

    root
      .querySelectorAll?.(
        '.custom-subscription-builder'
      )
      .forEach(
        initialiseBuilder
      );
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initialiseAllBuilders();
      }
    );
  } else {
    initialiseAllBuilders();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialiseAllBuilders(
        event.target
      );
    }
  );
})();